/**
 * TopBar — fixed top header strip.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/auth'
import { useTenantStore } from '../../stores/tenant'
import SyncBar from '../shared/SyncBar'
import ProfileModal from './modals/ProfileModal'

interface TopBarProps {
  height: number
  onPostingClick?: () => void
  onGatehouseClick?: () => void
}

export default function TopBar({ height, onPostingClick, onGatehouseClick }: TopBarProps) {
  const navigate = useNavigate()
  const session  = useAuthStore(s => s.session)
  const signOut  = useAuthStore(s => s.signOut)
  const tenant   = useTenantStore(s => s.tenant)
  const cadEnabled = useTenantStore(s => s.features.cad)
  const [showProfile, setShowProfile] = useState(false)

  const tenantName = tenant?.name ?? 'Fair Oaks Mall Security'
  const tenantLogo = tenant?.config.logoUrl

  function handlePosting() {
    if (onPostingClick) onPostingClick()
    else navigate('/posting')
  }

  const barStyle: React.CSSProperties = {
    position: 'fixed', top: 0, left: 0, right: 0,
    background: 'var(--bg-surface)',
    borderBottom: '1px solid var(--border)',
    padding: '0 16px',
    height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 901,
  }

  const btnStyle = (color: string, border: string): React.CSSProperties => ({
    background: color, border, borderRadius: 6,
    fontSize: 11, fontWeight: 700, padding: '4px 12px', cursor: 'pointer',
  })

  return (
    <div style={barStyle}>
      {/* Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {tenantLogo
          ? <img src={tenantLogo} alt={tenantName} style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'contain' }} />
          : <img src="./castle-logo.png" alt="Castellan" width={36} height={36} style={{ borderRadius: 6, objectFit: 'contain' }} />
        }
        <span style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 15, letterSpacing: 1 }}>CASTELLAN</span>
        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{tenantName}</span>
      </div>

      {/* Right side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <SyncBar />
        <button onClick={() => { const b = window.location.href.split('#')[0]; window.open(b + '#/dashboard', '_blank', 'noopener,noreferrer') }}
          style={btnStyle('rgba(59,130,246,0.15)', '1px solid rgba(59,130,246,0.4)')}
        ><span style={{ color: '#3b82f6' }}>🖥️ Dashboard</span></button>

        <button onClick={handlePosting}
          style={btnStyle('rgba(201,168,76,0.15)', '1px solid rgba(201,168,76,0.4)')}
        ><span style={{ color: 'var(--accent)' }}>📋 Posting</span></button>

        {/* Gatehouse (CAD) — only shown when the tenant has the cad feature.
            Separate-but-connected: lives as its own section but is reachable
            straight from the header so dispatch is always one click away. */}
        {cadEnabled && onGatehouseClick && (
          <button onClick={onGatehouseClick}
            style={btnStyle('rgba(220,90,90,0.15)', '1px solid rgba(220,90,90,0.45)')}
          ><span style={{ color: '#e05a5a' }}>📟 Gatehouse</span></button>
        )}

        <span
          onClick={() => setShowProfile(true)}
          title="Edit my profile"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 8, padding: '5px 10px',
            cursor: 'pointer',
          }}
        >
          <span style={{
            width: 26, height: 26, borderRadius: '50%',
            background: 'var(--accent)', color: '#1a2744',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 11, flexShrink: 0,
          }}>
            {(session?.callSign ?? '?').slice(-2)}
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 12 }}>
              {session?.name ? session.name.split(',')[0].trim() : session?.callSign}
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>
              {session?.callSign} · {session?.rank}
            </span>
          </span>
        </span>

        <button onClick={signOut}
          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-muted)', fontSize: 11, padding: '4px 12px', cursor: 'pointer' }}
        >Sign out</button>
      </div>

      {/* ProfileModal lives here — position:fixed so DOM location doesn't matter */}
      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
    </div>
  )
}
